/**
 * The small pieces the dated screens share.
 *
 * Kept together rather than split into a file each: a components directory with nine
 * one-export files in it is harder to read than one file with nine exports.
 *
 * THE NAME IS A LEFTOVER. These were the pieces the two internal screens shared; those
 * screens are gone and every consumer left - Overview, Calls, CallDetail, Settings - is
 * member-facing. Nothing in here is internal-only and nothing in here checks a role.
 */

import { useMemo } from "react";
import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { Segmented, Toggle, cx } from "../ui";
import { Phone } from "./Icons";
import { Empty, Skeleton, SkeletonRows, SkeletonTiles } from "./States";

const DAY = 24 * 60 * 60 * 1000;

const RANGES = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
];

/**
 * The window, in the URL rather than in component state.
 *
 * Same reason the calls list keeps its filters there: a view somebody wants to show
 * somebody else has to be a link. It also means one screen can hand off to another
 * without losing the range the reader was looking at.
 *
 * MEMOISED ON `days`, AND THAT IS NOT AN OPTIMISATION. `Date.now()` read in a render body
 * returns a different number every render, so the query string built from it is a new
 * string every render, so `useApi`'s effect - keyed on the path - tears down and refetches
 * every render, forever. The symptom is a page that renders its data and never stops
 * saying "Loading", while hammering the API in a loop. Pinning the window to the moment
 * the range was chosen is also the honest behaviour: a table whose end time slides while
 * you read it is a table whose totals change under you.
 */
export function useWindow(): { since: number; until: number; days: number } {
  const [params] = useSearchParams();
  const days = Number(params.get("days")) || 30;
  return useMemo(() => {
    const until = Date.now();
    return { since: until - days * DAY, until, days };
  }, [days]);
}

/**
 * How far back, and whether test calls count.
 *
 * Lives in the page HEAD rather than above the content, because it governs every number
 * on the screen below it. A range picker sitting among the filters reads as one more
 * filter; sitting beside the title it reads as the period the title refers to.
 */
export function RangePicker({ withTestToggle = true }: { withTestToggle?: boolean }) {
  const [params, setParams] = useSearchParams();
  const days = Number(params.get("days")) || 30;

  const set = (key: string, value: string) =>
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });

  return (
    <>
      {withTestToggle && (
        <Toggle
          checked={params.get("test") === "true"}
          onChange={(on) => set("test", on ? "true" : "")}
        >
          Include test calls
        </Toggle>
      )}
      <Segmented
        label="Date range"
        value={days}
        options={RANGES}
        onChange={(next) => set("days", String(next))}
      />
    </>
  );
}

/* --- the stat strip ---------------------------------------------------------------------- */

/**
 * The headline numbers, as ONE card divided rather than as five cards in a row.
 *
 * WHY THEY ARE FUSED. Separate tiles put a 12px trough between every pair of numbers, and
 * on a strip where the numbers are the page's first sentence - answered, handled, booked,
 * quoted, outstanding - those troughs read as five unrelated facts. Inside one card with
 * hairlines between them they read as one row that adds up, which is what an owner is
 * actually doing with them.
 *
 * IT TAKES AN ARRAY AND NOT CHILDREN, and that is not a style preference: the strip has to
 * know its own COUNT and its own weights to build a column template, and a component handed
 * `children` cannot know either. The count is genuinely variable - `data.value` on the
 * overview returns one cell per money basis and may return none - so anything from four
 * cells to seven arrives here.
 *
 * THE DIVIDERS ARE GAPS, NOT BORDERS. A 1px gap over a `line-soft` card paints a hairline
 * between every pair of cells in BOTH axes for free. A `border-l` with a first-child
 * exception looks identical until the strip wraps to two rows, at which point every row's
 * leftmost cell keeps a rule that should not be there and the row above has no rule under
 * it at all.
 *
 * THE GRID ITSELF IS `.strip` IN styles.css, and the reason is written there: three column
 * regimes plus a rule about the last cell, resolved in an order this file cannot control
 * from a class string. All that crosses the boundary is `--strip-cols`, which is the one
 * part built from the data.
 */
export interface StatCell {
  key: string;
  label: string;
  value: string;
  note?: ReactNode;
  /** Makes the whole cell a link into the calls list with the filter already applied. */
  href?: string;
  tone?: "alert";
  /** Relative width in the single-row layout. A money figure wants more than a count. */
  weight?: number;
  /** A value that is a string of money rather than a short number: one type step down. */
  wide?: boolean;
  /** Nothing happened, so the figure is quiet rather than a bold zero. */
  zero?: boolean;
}

export function Stats({ cells }: { cells: StatCell[] }) {
  return (
    <section
      className="strip mb-5 overflow-hidden rounded-xl border border-line bg-line-soft"
      style={{
        ["--strip-cols" as string]: cells.map((cell) => `${cell.weight ?? 1}fr`).join(" "),
      }}
    >
      {cells.map((cell) => (
        <StatBox key={cell.key} cell={cell} />
      ))}
    </section>
  );
}

/*
  THE SURFACE IS NOT IN HERE, and that is not tidiness. `bg-paper` and `bg-alert-wash` are
  the same Tailwind utility group, so which one wins is decided by the order Tailwind emits
  them and not by the order `cx` joins them - the attention cell rendered white for exactly
  that reason. One `?:` picks one class, and there is nothing left to collide.
*/
const CELL = "flex flex-col gap-2.5 px-6 py-5.5";
const CELL_LABEL = "text-xs font-medium text-ink-soft";
const CELL_NOTE = "text-xs leading-normal text-ink-faint";

function StatBox({ cell }: { cell: StatCell }) {
  const alert = cell.tone === "alert";
  const body = (
    <>
      <span className={cx(CELL_LABEL, alert && "flex items-center gap-1.5 text-alert")}>
        {alert && <span aria-hidden="true" className="size-[7px] rounded-full bg-alert" />}
        {cell.label}
      </span>
      <span
        className={cx(
          "nums leading-none font-medium tracking-[-0.03em]",
          cell.wide ? "text-4xl whitespace-nowrap" : "text-5xl",
          alert ? "text-alert" : cell.zero && "text-ink-faint",
        )}
      >
        {cell.value}
      </span>
      {cell.note && (
        <span className={cx(CELL_NOTE, alert && "font-medium text-alert")}>{cell.note}</span>
      )}
    </>
  );
  const className = cx(
    CELL,
    alert ? "bg-alert-wash" : "bg-paper",
    // A cell that goes somewhere says so on hover and nowhere else: a permanent chevron on
    // six cells is six pieces of furniture competing with six numbers.
    cell.href && !alert && "transition-colors hover:bg-accent-soft",
    cell.href && alert && "transition-opacity hover:opacity-80",
  );
  return cell.href ? (
    <Link className={className} to={cell.href}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
}

/**
 * The three things that can be true instead of the page.
 *
 * The wait is drawn as the shape of the answer - tiles, then a block - so the layout does
 * not jump when the data lands.
 */
export function State({
  loading,
  error,
  empty,
  shape = "tiles",
}: {
  loading: boolean;
  error: Error | null;
  empty: boolean;
  /**
   * WHICH shape the wait is drawn as.
   *
   * The rule in `States.tsx` is that a wait is drawn as the shape of the answer, and this
   * component was quietly breaking it on one of its two callers: `/settings` is a stack of
   * panels with no tiles anywhere on it, and it was opening with five tile skeletons and
   * then throwing them away. A skeleton that is the wrong shape is worse than no skeleton,
   * because the page jumps exactly as far as the skeleton was wrong.
   */
  shape?: "tiles" | "panels";
}) {
  if (error) {
    return <Empty tone="alert" title="Couldn't load this" body={error.message} />;
  }
  if (loading) {
    return shape === "tiles" ? (
      <>
        <SkeletonTiles />
        <Skeleton height={300} />
      </>
    ) : (
      <div className="grid gap-3.5">
        <Skeleton height={200} />
        <SkeletonRows rows={4} />
      </div>
    );
  }
  if (empty) {
    return (
      <Empty
        icon={Phone}
        title="No calls in this window"
        body="Nothing was answered in the period you've selected."
        /*
         * NAMES THE TWO CONTROLS, rather than describing them. The old copy - "try a
         * longer range, or include test calls" - is the same two suggestions, but a
         * reader who has not yet noticed that the range picker is up in the page head
         * has nowhere to take them. Both controls are in the head, and saying so is the
         * difference between advice and a way out.
         */
        hint="The range picker and the test-call toggle are both at the top of this page."
      />
    );
  }
  return null;
}

/**
 * The two-column body: the wider half on the left, the narrower on the right.
 *
 * SEVEN TO FIVE, NOT ONE TO ONE. The left column holds the three panels that are read - a
 * ranked list with a verbatim quote in it, a 168-cell heatmap, a ring with a legend - and
 * the right holds the three that are glanced at. An even split gives the heatmap columns
 * nine pixels each and leaves the week chart with room it does not use.
 *
 * The stacks INSIDE each column carry the gap, so a panel does not need a margin of its
 * own; see `Figure` in charts.tsx.
 */
export function Columns({ children }: { children: ReactNode }) {
  return (
    <div
      className={cx(
        "grid items-start gap-5 [&>*]:min-w-0",
        "grid-cols-[minmax(0,7fr)_minmax(0,5fr)]",
        "max-[1180px]:grid-cols-[minmax(0,1fr)]",
      )}
    >
      {children}
    </div>
  );
}

/** One column of the pair: panels stacked, evenly spaced, nothing else. */
export function Stack({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-5">{children}</div>;
}
